import React, { useContext, useState } from 'react';
import './ViewAllAdvances.css';
import VouchersDisplay from '../Vouchers/VouchersDisplay.jsx';
import Voucher from '../Voucher/Voucher.jsx';
import { CategoriesContext, CategoriesCompleteContext } from '../../services/context/CategoriesContext.js';
import { MoneyFormatterContext } from '../../services/context/MoneyFormatterContext.js';
import { createIsAdvanceOptionFilter } from '../../services/EligibilityOffers.js';
import { EligibilityContext } from '../../services/context/EligibilityContext.js';
import { useCostSort } from '../../services/hooks/useCostSort.js';
import { ConfigContext } from '../../services/context/ConfigContext.js';
import StockStatus from '../../services/Constants/StockStatus.js';
import TopBack from '../TopBack/TopBack.jsx';
import Slider from '../Slider/Slider.jsx';
import { AdvanceContext, UpdateAdvanceContext } from '../../services/context/AdvanceContext.js';
import AdvanceDateDetails from '../AdvanceDateDetails/AdvanceDateDetails.jsx';
import ProductVoucher from '../ProductVoucher/ProductVoucher.jsx';
import InfoIcon from '../../assets/InfoIcon.jsx';

/**
 * @returns {JSX.Element} ViewAllAdvances Component
 */
export default function ViewAllAdvances() {
  const categories = useContext(CategoriesContext);
  const categoriesComplete = useContext(CategoriesCompleteContext);
  const MoneyFormatter = useContext(MoneyFormatterContext);
  const vendors = categories.flatMap(category => category.vendors);

  const advanceVouchers = vendors.flatMap(vendor => vendor.vouchers.map(voucher => ({
    ...voucher,
    vendorIconUrl: vendor.iconUrl,
    vendorName: vendor.name,
  })));

  const eligibility = useContext(EligibilityContext);
  const seen = new Set();
  const { costSort } = useCostSort();
  const config = useContext(ConfigContext);
  const advancing = useContext(AdvanceContext);
  const setAdvancing = useContext(UpdateAdvanceContext);
  const [currentAdvance, setCurrentAdvance] = useState(null);

  const isAdvance = voucher => {
    const isAdvanceOption = createIsAdvanceOptionFilter(eligibility, voucher, MoneyFormatter);
    return Boolean(voucher?.options?.advance?.terms?.options?.some(option => isAdvanceOption(option)));
  };

  const handleAdvance = voucher => {
    setAdvancing({
      voucher,
      date: null,
    });
  };

  const closeAdvance = () => {
    setAdvancing(null);
  };

  const getAdvanceActionItem = voucher => {
    if (config.frontend.general.maintenanceWarning.flows.advance) {
      return (
        <p className='warning container-vh'>
          <InfoIcon /> Currently unavailable
        </p>
      );
    }

    return voucher.stock.status === StockStatus.inStock ? (
      <button
        disabled={ config.frontend.general.maintenanceWarning.flows.advance }
        className='secondary-button viewAllAdvances-advanceMe'
        onClick={ () => handleAdvance(voucher) }
      >
        Advance now
      </button>
    ) : (
      <>Out of stock</>
    );
  };

  const skeletonVouchers = [
    0,
    1,
    2,
  ];

  const advances
    = !advanceVouchers || advanceVouchers.length === 0
      ? []
      : advanceVouchers
        .filter(value => isAdvance(value))
        .filter(voucher => eligibility?.tcl
              && MoneyFormatter.getValue(voucher.cost).amount <= eligibility.tcl.amount)
        .sort((a, b) => {
          const nameCompare = a.vendorName.localeCompare(b.vendorName);
          return nameCompare !== 0 ? nameCompare : costSort(a, b);
        })
        .filter(voucher => {
          const key = `${voucher.vendorName}-${voucher.cost?.ZAR}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map(voucher => (
          <Voucher
            key={ voucher.id }
            iconUrl={ voucher.vendorIconUrl }
            onClick={ () => setCurrentAdvance(voucher) }
            title={
              <>
                <div className='viewAllAdvances-vendorName'>{voucher.vendorName}</div>
                <div className='pay-get'>
                  Get{' '}
                  <em>
                    {MoneyFormatter.format(MoneyFormatter.getValue(voucher?.options?.advance?.value))}
                  </em>
                </div>
              </>
            }
            addedValue={ <span>Pay later</span> }
            actionItem={ getAdvanceActionItem(voucher) }
            isAdvance
          />
        ));

  const displayAdvanceDateSelection = () => advancing && (
    <Slider
      onClickOutside={ closeAdvance }
      slot={ <AdvanceDateDetails /> }
    />
  );

  const displayAdvanceDetails = () => currentAdvance && (
    <Slider
      onClickOutside={ () => setCurrentAdvance(null) }
      slot={
        <ProductVoucher
          descriptionType='advance'
          voucher={ currentAdvance }
          actionItem={ getAdvanceActionItem(currentAdvance) }
        />
      }
    />
  );

  return (
    <div className='view-all-advances'>
      <TopBack />
      <div className='viewAllAdvances-header-container'>
        <span className='viewAllAdvances-header'>Offers you can get now</span>
        <span className='viewAllAdvances-subheader'>
          Use your available funds today and pay later
        </span>
      </div>
      {
        eligibility?.tcl && (
          <VouchersDisplay
            title={ <h4>Buy now, pay later</h4> }
            onLoading={
              <>
                {
                  skeletonVouchers.map(number => (
                    <Voucher key={ number } />
                  ))
                }
              </>
            }
            isLoading={ !categoriesComplete }
            vouchers={ advances }
          />
        )
      }
      {displayAdvanceDetails()}
      {displayAdvanceDateSelection()}
    </div>
  );
}
